import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  LearningPositionSpineFamilyTrace,
  LearningPositionSpineGraph,
  LearningPositionSpineGraphNode,
  LearningPositionSpineNodeStatus,
  LearningPositionSpineUnmappedFootprint,
} from '../../services/reviewService';
import { sanitizeUserFacingBindingText } from '../../utils/subjectValueBinding';

type SubjectSpinePositionGraphProps = {
  graph: LearningPositionSpineGraph;
  onFamilyPress?: (family: LearningPositionSpineFamilyTrace) => void;
};

type DisplayItem =
  | { kind: 'node'; node: LearningPositionSpineGraphNode; key: string }
  | { kind: 'idle-run'; nodes: LearningPositionSpineGraphNode[]; key: string; collapsed: boolean };

const STATUS_META: Record<LearningPositionSpineNodeStatus, { label: string; color: string; fill: string }> = {
  idle: { label: '未刷', color: '#8b909a', fill: '#f0f2f5' },
  visited: { label: '已刷', color: '#2f6fed', fill: '#EAF2FF' },
  proficient: { label: '掌握', color: '#2e7d32', fill: '#E8F5E8' },
  fuzzy: { label: '模糊', color: '#ed6c02', fill: '#FFF3E0' },
  forgotten: { label: '遗忘', color: '#d32f2f', fill: '#FDECEC' },
};

function formatDateTime(iso: string | null) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getMonth() + 1}/${date.getDate()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function isIdleNode(node: LearningPositionSpineGraphNode) {
  return node.status === 'idle';
}

function buildDisplayItems(
  nodes: LearningPositionSpineGraphNode[],
  isRunExpanded: (runKey: string) => boolean,
): DisplayItem[] {
  const items: DisplayItem[] = [];
  let index = 0;

  while (index < nodes.length) {
    const node = nodes[index];
    if (!isIdleNode(node)) {
      items.push({ kind: 'node', node, key: node.nodeId });
      index += 1;
      continue;
    }

    const runStart = index;
    while (index < nodes.length && isIdleNode(nodes[index])) {
      index += 1;
    }

    const run = nodes.slice(runStart, index);
    const runKey = `idle-${run[0].nodeId}-${run[run.length - 1].nodeId}`;

    if (run.length >= 2 && !isRunExpanded(runKey)) {
      items.push({ kind: 'idle-run', nodes: run, key: runKey, collapsed: true });
      continue;
    }

    for (const runNode of run) {
      items.push({ kind: 'node', node: runNode, key: runNode.nodeId });
    }
  }

  return items;
}

function countCollapsibleIdleRuns(nodes: LearningPositionSpineGraphNode[]) {
  let count = 0;
  let index = 0;

  while (index < nodes.length) {
    if (!isIdleNode(nodes[index])) {
      index += 1;
      continue;
    }

    const runStart = index;
    while (index < nodes.length && isIdleNode(nodes[index])) {
      index += 1;
    }

    if (index - runStart >= 2) {
      count += 1;
    }
  }

  return count;
}

function SpineNodeRow({
  node,
  isFirst,
  isLast,
  familyExpanded,
  onToggleFamilies,
}: {
  node: LearningPositionSpineGraphNode;
  isFirst: boolean;
  isLast: boolean;
  familyExpanded: boolean;
  onToggleFamilies: () => void;
}) {
  const statusMeta = STATUS_META[node.status];

  return (
    <div className="flex gap-3">
      <div className="flex w-[18px] flex-col items-center">
        {!isFirst ? <div className="w-0.5 flex-1 bg-border" /> : <div className="flex-1" />}
        <div className="h-3 w-3 rounded-full border-2" style={{ backgroundColor: statusMeta.color, borderColor: statusMeta.color }} />
        {!isLast ? <div className="w-0.5 flex-1 bg-border" /> : <div className="flex-1" />}
      </div>

      <button
        type="button"
        onClick={node.footprintCount > 0 ? onToggleFamilies : undefined}
        className={`mb-2 flex-1 rounded-xl border border-border p-4 text-left transition ${
          node.footprintCount > 0 ? 'hover:opacity-95' : 'cursor-default'
        }`}
        style={{ backgroundColor: statusMeta.fill }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-primary">#{node.nodeNo}</div>
            <div className="mt-0.5 text-sm font-semibold text-ink">{node.title}</div>
          </div>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-xs font-bold"
            style={{ color: statusMeta.color, backgroundColor: `${statusMeta.color}22` }}
          >
            {statusMeta.label}
          </span>
        </div>

        {node.firstLevelModuleLabel ? (
          <p className="mt-1 text-xs text-muted">一级模块：{node.firstLevelModuleLabel}</p>
        ) : null}
        {node.moduleLabel && node.moduleLabel !== node.firstLevelModuleLabel ? (
          <p className="mt-1 text-xs text-muted">模块：{node.moduleLabel}</p>
        ) : null}

        <div className="mt-2 flex justify-between gap-2 text-xs text-muted">
          <span>{node.footprintCount > 0 ? `${node.footprintCount} 次刷卡` : '暂无历史轨迹'}</span>
          {node.latestCompletedAt ? <span>最近 {formatDateTime(node.latestCompletedAt)}</span> : null}
        </div>

        {familyExpanded && node.families.length > 0 ? (
          <div className="mt-3 space-y-1 border-t border-border pt-3">
            {node.families.slice(0, 4).map((family) => {
              const familyStatus = STATUS_META[family.feedback ? family.feedback : 'visited'];
              const title = family.particleTitle?.trim() || `卡族 ${family.particleId.slice(0, 8)}`;
              return (
                <div key={`${family.particleId}:${family.completedAt}`} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate text-ink">{title}</span>
                  <span className="shrink-0 font-semibold" style={{ color: familyStatus.color }}>
                    {familyStatus.label}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </button>
    </div>
  );
}

function CollapsedIdleRunRow({
  nodes,
  isFirst,
  isLast,
  onExpand,
}: {
  nodes: LearningPositionSpineGraphNode[];
  isFirst: boolean;
  isLast: boolean;
  onExpand: () => void;
}) {
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const rangeLabel = first.nodeNo === last.nodeNo ? `#${first.nodeNo}` : `#${first.nodeNo} - #${last.nodeNo}`;

  return (
    <div className="flex gap-3">
      <div className="flex w-[18px] flex-col items-center">
        {!isFirst ? <div className="w-0.5 flex-1 bg-border" /> : <div className="flex-1" />}
        <div className="h-2.5 w-2.5 rounded-full border-2 border-muted bg-[#f0f2f5]" />
        {!isLast ? <div className="w-0.5 flex-1 bg-border" /> : <div className="flex-1" />}
      </div>

      <button
        type="button"
        onClick={onExpand}
        className="mb-2 flex-1 rounded-xl border border-dashed border-border bg-[#f0f2f5] p-4 text-left transition hover:bg-[#e8ebf0]"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-ink">{rangeLabel}</span>
          <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs font-bold text-muted">
            未刷 ×{nodes.length}
          </span>
        </div>
        <p className="mt-1 truncate text-xs text-muted">
          {first.title}{nodes.length > 1 ? ` … ${last.title}` : ''}
        </p>
        <p className="mt-1 text-xs font-semibold text-primary">点击展开这段连续未刷节点</p>
      </button>
    </div>
  );
}

function UnmappedFootprintsPanel({
  footprints,
  onFamilyPress,
}: {
  footprints: LearningPositionSpineUnmappedFootprint[];
  onFamilyPress?: (family: LearningPositionSpineFamilyTrace) => void;
}) {
  if (!footprints.length) {
    return (
      <p className="mt-2 text-xs text-muted">
        暂无明细，请重新加载页面后再试。
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-2 border-t border-border pt-3">
      {footprints.map((footprint) => {
        const familyStatus = STATUS_META[footprint.feedback ? footprint.feedback : 'visited'];
        const title = footprint.particleTitle?.trim() || `卡族 ${footprint.particleId.slice(0, 8)}`;
        const rowClassName = 'flex w-full items-start justify-between gap-3 rounded-lg border border-border bg-surface p-3 text-left transition hover:bg-surface-accent/40';

        if (onFamilyPress) {
          return (
            <button
              key={`${footprint.particleId}:${footprint.completedAt}`}
              type="button"
              onClick={() => onFamilyPress(footprint)}
              className={rowClassName}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-primary">{title}</div>
                <div className="mt-1 text-xs leading-relaxed text-amber-700">{footprint.unmappedReasonLabel}</div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                <span className="font-semibold" style={{ color: familyStatus.color }}>
                  {familyStatus.label}
                </span>
                <span className="text-muted">{formatDateTime(footprint.completedAt)}</span>
                <span className="font-bold text-primary">详情</span>
              </div>
            </button>
          );
        }

        return (
          <div key={`${footprint.particleId}:${footprint.completedAt}`} className={rowClassName}>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-ink">{title}</div>
              <div className="mt-1 text-xs leading-relaxed text-amber-700">{footprint.unmappedReasonLabel}</div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
              <span className="font-semibold" style={{ color: familyStatus.color }}>
                {familyStatus.label}
              </span>
              <span className="text-muted">{formatDateTime(footprint.completedAt)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SubjectSpinePositionGraph({ graph, onFamilyPress }: SubjectSpinePositionGraphProps) {
  const [expandAll, setExpandAll] = useState(false);
  const [expandedRunKeys, setExpandedRunKeys] = useState<Set<string>>(() => new Set());
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [showUnmappedList, setShowUnmappedList] = useState(false);

  const unmappedFootprints = graph.unmappedFootprints ?? [];

  useEffect(() => {
    setShowUnmappedList(false);
  }, [graph.subject, graph.unmappedFootprintCount]);

  const collapsibleRunCount = useMemo(() => countCollapsibleIdleRuns(graph.nodes), [graph.nodes]);

  const isRunExpanded = useCallback(
    (runKey: string) => expandAll || expandedRunKeys.has(runKey),
    [expandAll, expandedRunKeys],
  );

  const displayItems = useMemo(
    () => buildDisplayItems(graph.nodes, isRunExpanded),
    [graph.nodes, isRunExpanded],
  );

  const activeNodes = useMemo(
    () => graph.nodes.filter((node) => node.footprintCount > 0).length,
    [graph.nodes],
  );

  const handleToggleExpandAll = useCallback(() => {
    if (expandAll) {
      setExpandAll(false);
      setExpandedRunKeys(new Set());
      setExpandedNodeId(null);
      return;
    }
    setExpandAll(true);
    setExpandedRunKeys(new Set());
  }, [expandAll]);

  const handleExpandRun = useCallback((runKey: string) => {
    setExpandAll(false);
    setExpandedRunKeys((current) => {
      const next = new Set(current);
      next.add(runKey);
      return next;
    });
  }, []);

  if (!graph.nodes.length) {
    return (
      <div className="rounded-xl border border-border bg-surface p-5">
        <h3 className="font-semibold text-ink">
          {graph.runtimeStatus === 'unavailable' ? '学科画像暂不可用' : '暂无学科主干'}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          {sanitizeUserFacingBindingText(graph.runtimeMessage)
            || (graph.totalFootprintCount > 0
              ? '已有历史轨迹，但暂时无法拉取学科画像主干。请确认学科画像服务可用，且相关卡族已完成学科节点绑定。'
              : '当前学科还没有可用的学科画像主干，或历史足迹尚未映射到主干节点。')}
        </p>
        {graph.unmappedFootprintCount > 0 ? (
          <>
            <button
              type="button"
              onClick={() => setShowUnmappedList((current) => !current)}
              className="mt-3 text-left text-xs font-semibold text-amber-700 hover:underline"
            >
              另有 {graph.unmappedFootprintCount} 次轨迹尚未绑定到学科主干节点。
              {showUnmappedList ? ' 点击收起' : ' 点击查看'}
            </button>
            {showUnmappedList ? (
              <UnmappedFootprintsPanel footprints={unmappedFootprints} onFamilyPress={onFamilyPress} />
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-[#EAF2FF]/60 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-ink">
              {graph.profileDisplayName || graph.subjectLabel}
              {graph.spineTitle ? ` · ${graph.spineTitle}` : ''}
            </h3>
            <p className="mt-1 text-sm text-muted">
              已映射 {graph.mappedFootprintCount}/{graph.totalFootprintCount} 次历史轨迹，
              覆盖 {activeNodes}/{graph.nodes.length} 个主干节点。
            </p>
          </div>
          {collapsibleRunCount > 0 ? (
            <button
              type="button"
              onClick={handleToggleExpandAll}
              className="shrink-0 rounded-full border border-border bg-surface px-3 py-1 text-xs font-bold text-primary hover:bg-primary/5"
            >
              {expandAll ? '全部折叠' : '全部展开'}
            </button>
          ) : null}
        </div>
        {graph.unmappedFootprintCount > 0 ? (
          <>
            <button
              type="button"
              onClick={() => setShowUnmappedList((current) => !current)}
              className="mt-2 text-left text-xs font-semibold text-amber-700 hover:underline"
            >
              另有 {graph.unmappedFootprintCount} 次轨迹尚未绑定到学科主干节点。
              {showUnmappedList ? ' 点击收起' : ' 点击查看'}
            </button>
            {showUnmappedList ? (
              <UnmappedFootprintsPanel footprints={unmappedFootprints} onFamilyPress={onFamilyPress} />
            ) : null}
          </>
        ) : null}
      </div>

      <div>
        {displayItems.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === displayItems.length - 1;

          if (item.kind === 'idle-run') {
            return (
              <CollapsedIdleRunRow
                key={item.key}
                nodes={item.nodes}
                isFirst={isFirst}
                isLast={isLast}
                onExpand={() => handleExpandRun(item.key)}
              />
            );
          }

          return (
            <SpineNodeRow
              key={item.key}
              node={item.node}
              isFirst={isFirst}
              isLast={isLast}
              familyExpanded={expandedNodeId === item.node.nodeId}
              onToggleFamilies={() => {
                setExpandedNodeId((current) => (current === item.node.nodeId ? null : item.node.nodeId));
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
